// Central copy dictionary — English (en-CA) and French (fr-CA).
// Every user-facing string on the site lives here (or in data/tools.ts
// which carries its own fr fields).

const en = {
  nav: {
    features: 'Features',
    pricing: 'Pricing',
    integrations: 'Integrations',
    tools: 'Free Tools',
    login: 'Log in',
  },
  footer: {
    tagline:
      'Online payroll for Canadian businesses — every province and territory, including Quebec. No contracts, no hidden fees.',
    product: 'Product',
    resources: 'Resources',
    company: 'Company',
    demo: 'Book a 15-min demo',
    partner: 'Pro Partners',
    privacy: 'Privacy statement',
    terms: 'Terms & conditions',
    rights: '© Websential Inc. All rights reserved.',
    mockup: 'Redesign concept mockup — placeholder content',
  },
  home: {
    h1a: 'Payroll for every Canadian province. ',
    h1b: 'Connected to everything you use.',
    sub: 'Calculate, pay and file — CRA-compliant payroll with direct deposit, automated remittances and one of the largest integration ecosystems in Canadian payroll.',
    demo: 'Book a 15-min demo',
    trust: 'No contracts · No hidden fees · Cancel anytime',
    panel: {
      title: 'Start your payroll',
      sub: 'Online payroll. Set up in minutes.',
      accountType: 'Choose account type',
      option1: 'Business owner',
      option2: 'Service Provider',
      name: 'Name',
      company: 'Company',
      email: 'Work email',
      submit: 'Get started',
      pad: 'Start right away with a credit card — no bank paperwork. A PAD co-signed by your bank is needed only to activate direct deposit and automated remittances.',
      legal: 'By continuing you accept the Subscription Agreement & Privacy Policy.',
    },
    stats: [
      'Provinces & territories covered',
      'Years serving Canadian businesses',
      'Downloadable payroll history',
      'Setup, contract or termination fees',
    ],
    clients: 'Trusted by Canadian businesses, coast to coast',
    workflow: {
      h: 'From timesheet to paycheque in three steps',
      sub: 'The whole pay run — calculated, compliant and filed — without leaving your browser.',
      step: 'Step',
      steps: [
        {
          title: 'Connect & import',
          body: 'Pull hours from your time & attendance or HR system — or enter them directly. Employee data stays in sync.',
        },
        {
          title: 'Calculate & comply',
          body: 'CPP, EI, federal and provincial tax calculated automatically — current tables for every province, including Quebec.',
        },
        {
          title: 'Pay & file',
          body: 'Direct deposit lands, remittances are filed, and T4 / T4A / RL-1 / ROE are e-filed. Pay stubs ready to print and share.',
        },
      ],
      foot1: 'One pay run. Three steps. ',
      foot2: 'Done before your coffee gets cold.',
    },
    eco: {
      h: 'Create your ecosystem',
      body: 'SimplePay connects seamlessly with leading HR, time & attendance, scheduling and accounting systems — one of the largest integration ecosystems in Canadian payroll.',
      link: 'View all integrations',
      caption: 'Accounting · HR · Time & attendance · Scheduling — plus open API',
      yourApp: 'Your app · API',
    },
    bento: {
      h: "Everything payroll demands. Nothing it doesn't.",
      heroTitle: 'Compliance ready',
      heroBody:
        'On-demand compliance and e-filing for every province, including Quebec — year-end slips and Records of Employment generated and filed straight from your pay run.',
      provLabel: 'Every province & territory',
      docChips: ['T4', 'T4A', 'RL-1', 'ROE', 'CRA e-file', 'Revenu Québec'],
      cards: [
        {
          title: 'Electronic payments',
          body: 'Direct deposit payroll, with CRA remittances calculated and paid automatically every run.',
        },
        {
          title: 'All the rules, current',
          body: 'Federal and provincial tax tables, statutory holiday rules and remittance schedules — updated the moment they change.',
        },
        {
          title: 'Your data, owned',
          body: 'Export payslips, journals and full history in multiple formats, whenever you want. No lock-in.',
        },
        {
          title: 'Map your system',
          body: 'Shape payroll to your books with user-generated and pre-defined mapping options.',
        },
        {
          title: 'Easy relationship',
          body: 'Unlimited support from real payroll people, included with your subscription.',
        },
      ],
      link: 'Explore all features',
    },
    toolsH: 'Try the math before you sign up',
    openTool: 'Open tool',
    cta: {
      h: 'See it on your own payroll',
      sub: "A 15-minute walkthrough and Q&A — we'll run it on your own payroll numbers.",
      button: 'Book a 15-min demo',
    },
  },
  features: {
    h: 'Built for Canadian payroll, end to end',
    sub: 'From raw hours to filed remittances — without spreadsheets or CRA guesswork.',
    highlights: [
      {
        kicker: 'Pay day, your way',
        title: 'Pay every employee the way they prefer',
        body: 'Pay everyone the way that works for them — direct deposit to any Canadian bank account, a printed cheque, or a mix of both in the same pay run. Set each employee\u2019s preference once and SimplePay applies it every pay period, so pay day runs itself.',
        points: [
          'Direct deposit to any Canadian bank account',
          'Print-ready cheques on standard cheque stock',
          'Mix methods within one pay run — deposit for some, cheques for others',
          'Change a pay mid-collection — refunds keep the pay date',
        ],
      },
      {
        kicker: 'Stat holidays, solved',
        title: 'Statutory holiday pay, calculated for you',
        body: 'Every province counts stat holiday pay differently — and Quebec counts it differently again. SimplePay applies the right rule for each employee\u2019s province automatically, so a holiday Monday never turns into a spreadsheet Sunday.',
        points: [
          'Provincial rules built in — including Quebec',
          'Eligible hours & average pay handled automatically',
          'Works for hourly, salary, and mixed teams',
          'Answers, not lookup tables',
        ],
      },
      {
        kicker: 'Compliance on demand',
        title: 'Every province. Every rule. Always current.',
        body: 'Federal and provincial tax tables, CPP/CPP2, EI, and filing requirements are maintained for you — for all provinces and territories, including Quebec (RL-1, CNESST). Print or e-file compliance documents whenever you need them, right from the pay run.',
        points: [
          'T4 / T4A / RL-1 / ROE e-filing',
          'Print or electronic — your choice',
          'CPP2 & latest CRA updates',
          'Quebec fully supported, not bolted on',
        ],
      },
      {
        kicker: 'Remittances on autopilot',
        title: 'CRA, RQ & EHT remittances — filed on your schedule',
        body: 'Your remittance frequency isn\u2019t your payroll frequency, and SimplePay knows it. Monthly, quarterly, or Threshold 1 / Threshold 2 — remittances are calculated from every pay run and filed on the schedule the CRA assigned you.',
        points: [
          'Monthly, quarterly, Threshold 1 & 2',
          'CRA and Revenu Québec on separate schedules',
          'EHT handled alongside source deductions',
          'Filed for you — no CRA portal trips',
        ],
      },
      {
        kicker: 'ROEs without the panic',
        title: 'ROEs issued inside the 5-day deadline',
        body: 'When someone leaves, the clock starts: Service Canada expects the ROE within 5 business days. SimplePay flags the termination, tracks the deadline, and hands you a ready-to-file document — or prepares it for you as a downloadable PDF, usually within a day.',
        points: [
          'Deadline tracked from the termination date',
          'Print or submit electronically',
          'SimplePay-managed option · $3 per ROE',
          'Full history, always retrievable',
        ],
      },
      {
        kicker: 'Your payroll, mapped your way',
        title: 'Custom earnings, deductions & reimbursements',
        body: 'Start from pre-defined payroll items or build your own — taxable benefits, allowances, garnishments, expense reimbursements. Formulas can populate payroll columns automatically, so recurring calculations enter themselves.',
        points: [
          'Pre-defined & custom payroll items',
          'Formulas that populate payroll columns',
          'Taxable benefits & reimbursements handled right',
          'Set once — applied every run',
        ],
      },
      {
        kicker: 'Built to scale',
        title: 'Locations & structure for growing teams',
        body: 'Organize employees by location, department, or entity and report at any level. Head office sees the whole picture; location managers see their own — with access roles to match.',
        points: [
          'Multi-location organization',
          'Roll-up & per-location reporting',
          'Custom access roles',
          'Works alongside your accountant\u2019s view',
        ],
      },
      {
        kicker: 'Job costing, end to end',
        title: 'Timesheets in. Job-costed journals out.',
        body: 'Import timesheets with job and project codes from your scheduling tools, then export payroll to accounting with the same codes attached. Labour costs land on the right job in QuickBooks or Xero — without re-keying a single hour.',
        points: [
          'Timesheet import with job / project codes',
          'Job-costed journal entries to accounting',
          'QuickBooks Online, Xero & more',
          'Custom GL mapping included',
        ],
      },
    ],
    placeholder: 'Product screenshot placeholder',
    chip1: 'One pay run · 3 cheques + 1 direct deposit',
    includedH: 'One price. Everything included.',
    includedSub: 'No tiers. No add-on paywalls for the essentials.',
    included: [
      'Unlimited support',
      'Addon integrations',
      'API access',
      'Statutory holiday calculator',
      'Direct deposit',
      'Custom earnings & deductions',
      'General ledger import',
    ],
    cta: 'See pricing',
  },
  pricing: {
    h: 'Fair and transparent pricing',
    sub: '$2 per employee per pay run. No contracts, no setup or termination costs, no annual fees.',
    perEmployee: ' / employee / pay run',
    minLabel: (min: number) => `Minimum billing $${min} per pay run`,
    tiers: [
      {
        name: 'Standard',
        blurb: 'Everything you need to run payroll — done right, simply.',
        badge: '',
        features: [
          'Direct deposit payroll',
          'All provinces & territories, including Quebec',
          'T4, T4A, RL-1 & ROE e-filing',
          'Statutory holiday calculator',
          'Integrations & API access',
          'Unlimited support',
          '7 years downloadable history',
        ],
      },
      {
        name: 'Professional',
        blurb: 'For teams running jobs and projects through payroll.',
        badge: 'For project-based teams',
        features: [
          'Everything in Standard',
          'Job / project import from timesheets',
          'Job / project-level journal export to your accounting app',
          'Create custom access roles',
          'Create formulas to populate payroll columns',
          'Enterprise structure for locations',
        ],
      },
    ],
    est: {
      h: 'Estimate your cost',
      sub: 'Real numbers, your payroll — no email required.',
      plan: 'Plan',
      minRun: (min: number) => `$${min} min / run`,
      freq: 'Payroll frequency',
      frequencies: ['Monthly', 'Semi-monthly', 'Bi-weekly', 'Weekly'],
      employees: 'Employees paid per run',
      remit: 'Remittances by SimplePay',
      remitNote: '(optional, $5 each per remittance)',
      remitFreq: 'Remittance frequency',
      remitTypes: ['CRA', 'EHT', 'RQ'],
      remitFrequencies: ['Quarterly', 'Monthly', 'Threshold 1', 'Threshold 2'],
      perRun: 'Per pay run',
      perYear: (label: string, runs: number) => `Per year · ${label.toLowerCase()} (${runs} runs)`,
      minApplies: (emp: number, per: number, min: number, tier: string) =>
        `${emp} × $2 = $${per} → $${min} ${tier} minimum applies`,
      normal: (emp: number, per: number) => `${emp} employees × $2 = $${per}`,
      annualRemit: (n: number, total: number) =>
        `includes $${total} remittances (${n} × $5 at their own frequencies)`,
      foot: 'Only employees paid in a run are billed (cheque or direct deposit) — inactive employees are never charged. ROE by SimplePay is $3 per ROE issued, only when used.',
    },
    optional: {
      h: 'Optional services',
      remitName: 'Remittances by SimplePay',
      remitNote: 'CRA, EHT and RQ — each remittance billed separately',
      remitPer: 'per remittance',
      roeName: 'ROE by SimplePay',
      roeNote: 'Only when you ask us to issue one',
      roePer: 'per ROE issued',
    },
    includedH: 'Included with every subscription',
    included: [
      'Unlimited support',
      'Addons integration',
      'API access',
      'Statutory holiday calculator',
      'Direct deposit',
    ],
    noFees: {
      h: 'No other fees. Period.',
      body: 'No setup, contract, termination, annual compliance or inactive payroll fees — those are gone for good.',
    },
    cta: {
      h: 'Run the numbers on your own payroll',
      sub: "Start today — or book a 15-minute walkthrough and we'll price it out together.",
      start: 'Get started',
      demo: 'Book a 15-min demo',
    },
  },
  integrations: {
    h: 'Connect. Control. Succeed.',
    sub: (count: number) =>
      `SimplePay seamlessly connects to the leading HR, time tracking, scheduling and accounting systems — ${count} integrations and counting.`,
    cats: [
      { name: 'HR', blurb: 'Keep employee records in sync — no double entry.' },
      { name: 'Accounting', blurb: 'Push payroll journals straight into your ledger.' },
      { name: 'Time, Attendance & Scheduling', blurb: 'Approved hours flow straight into the pay run.' },
      { name: 'Year-End & Filing', blurb: 'Close the year without the spreadsheet gymnastics.' },
    ],
    apps: (n: number) => `${n} ${n === 1 ? 'app' : 'apps'}`,
    website: 'Website',
    guide: 'Integration guide',
    capabilities: {
      'Import / export employees': 'Import / export employees',
      'Sync employee data': 'Sync employee data',
      'Import timesheets': 'Import timesheets',
      'Import employees': 'Import employees',
      'Payroll journal with multi-wage accounts': 'Payroll journal with multi-wage accounts',
      'Import / export employees and sync': 'Import / export employees and sync',
      'Single Sign-On (SSO)': 'Single Sign-On (SSO)',
      'Tracking categories': 'Tracking categories',
      'Export payroll journal': 'Export payroll journal',
      'Payroll journal (desktop & online)': 'Payroll journal (desktop & online)',
      'Import timesheets (+ scheduling)': 'Import timesheets (+ scheduling)',
      'Export payroll year-to-date': 'Export payroll year-to-date',
      'T4 / RL-1 filing': 'T4 / RL-1 filing',
      PIER: 'PIER',
    } as Record<string, string>,
    cta: {
      h: "Don't see your favourite app?",
      sub: 'Get in touch — with custom mapping and open API access, chances are we can connect it.',
      button: 'Get in touch',
    },
  },
  tools: {
    h: 'Payroll utilities, no sign-up required',
    sub: 'The same calculation engine that powers SimplePay — free to use.',
    openTool: 'Open tool',
    cta: {
      h: 'Like the math? Automate the rest.',
      sub: 'The calculator does one pay run. SimplePay does all of them — plus the filing, the deposits and the records.',
      button: 'See pricing',
    },
  },
  toolPage: {
    back: 'All free tools',
    workspace: (title: string) => `${title} — workspace`,
    workspaceNote:
      'The interactive tool from simplepay.ca will be embedded on this page in the final build.',
    does: 'What it does',
    audience: "Who it's for",
    more: 'More free tools',
    openTool: 'Open tool',
    cta: {
      h: 'Like the math? Automate the rest.',
      sub: 'This tool does one job. SimplePay does the whole payroll — filing, deposits and records included.',
      button: 'See pricing',
    },
  },
  login: {
    title: 'Log in to SimplePay',
    sub: 'Welcome back. Pick up right where your payroll left off.',
    email: 'Work email',
    password: 'Password',
    remember: 'Remember me',
    forgot: 'Forgot password?',
    submit: 'Log in',
    noAccount: 'New to SimplePay?',
    start: 'Start your payroll',
  },
  bookDemo: {
    title: 'Book your 15-minute demo',
    sub: 'See SimplePay on your own payroll — a screen-share walkthrough, no slides.',
    name: 'Name',
    email: 'Work email',
    company: 'Company',
    time: 'Preferred time',
    timeOptions: ['Weekday morning', 'Weekday afternoon', 'Weekday evening'],
    submit: 'Request my demo',
    note: '15 minutes · Screen-share · No slides',
    alt: 'Prefer email?',
  },
  pro: {
    h: 'SimplePay Pro Partners',
    sub: 'Accountants, bookkeepers and service providers who run payroll on SimplePay — support that goes beyond software and keeps your business on track.',
    filterAll: 'All',
    visit: 'Visit website',
    become: {
      h: 'Run payroll for your clients?',
      sub: 'Join the Pro Partner program — manage multiple clients from one account, with partner pricing and priority support.',
      button: 'Become a Pro Partner',
    },
  },
}

export type Translation = typeof en

const fr: Translation = {
  nav: {
    features: 'Fonctionnalités',
    pricing: 'Tarifs',
    integrations: 'Intégrations',
    tools: 'Outils gratuits',
    login: 'Connexion',
  },
  footer: {
    tagline:
      'La paie en ligne pour les entreprises canadiennes — toutes les provinces et tous les territoires, y compris le Québec. Sans contrat, sans frais cachés.',
    product: 'Produit',
    resources: 'Ressources',
    company: 'Entreprise',
    demo: 'Réserver une démo de 15 min',
    partner: 'Partenaires Pro',
    privacy: 'Déclaration de confidentialité',
    terms: 'Conditions générales',
    rights: '© Websential inc. Tous droits réservés.',
    mockup: 'Maquette conceptuelle — contenu provisoire',
  },
  home: {
    h1a: 'La paie pour chaque province canadienne. ',
    h1b: 'Connectée à tout ce que vous utilisez.',
    sub: "Calculez, payez et déclarez — une paie conforme à l'ARC avec dépôt direct, remises automatisées et l'un des plus vastes écosystèmes d'intégration de la paie canadienne.",
    demo: 'Réserver une démo de 15 min',
    trust: 'Sans contrat · Sans frais cachés · Annulez en tout temps',
    panel: {
      title: 'Lancez votre paie',
      sub: 'La paie en ligne. Prête en quelques minutes.',
      accountType: 'Choisissez le type de compte',
      option1: "Propriétaire d'entreprise",
      option2: 'Fournisseur de services',
      name: 'Nom',
      company: 'Entreprise',
      email: 'Courriel professionnel',
      submit: 'Commencer',
      pad: "Commencez immédiatement avec une carte de crédit — sans documents bancaires. Une entente de débit préautorisé (DPA) cosignée par votre banque est requise uniquement pour activer le dépôt direct et les remises automatisées.",
      legal: "En continuant, vous acceptez l'Entente d'abonnement et la Politique de confidentialité.",
    },
    stats: [
      'Provinces et territoires desservis',
      'Années au service des entreprises canadiennes',
      'Historique de paie téléchargeable',
      "Frais d'installation, de contrat ou de résiliation",
    ],
    clients: "Des entreprises canadiennes nous font confiance, d'un océan à l'autre",
    workflow: {
      h: 'De la feuille de temps au chèque de paie en trois étapes',
      sub: 'Tout le cycle de paie — calculé, conforme et déclaré — sans quitter votre navigateur.',
      step: 'Étape',
      steps: [
        {
          title: 'Connectez et importez',
          body: 'Récupérez les heures de votre système de temps et présences ou de RH — ou saisissez-les directement. Les données des employés restent synchronisées.',
        },
        {
          title: 'Calculez et conformez-vous',
          body: 'RPC, AE, impôt fédéral et provincial calculés automatiquement — tables à jour pour chaque province, y compris le Québec.',
        },
        {
          title: 'Payez et déclarez',
          body: 'Le dépôt direct est versé, les remises sont produites et les T4 / T4A / RL-1 / RE sont transmis par voie électronique. Talons de paie prêts à imprimer et à partager.',
        },
      ],
      foot1: 'Un cycle de paie. Trois étapes. ',
      foot2: 'Terminé avant que votre café ne refroidisse.',
    },
    eco: {
      h: 'Créez votre écosystème',
      body: "SimplePay se connecte en toute transparence aux principaux systèmes de RH, de temps et présences, de planification et de comptabilité — l'un des plus vastes écosystèmes d'intégration de la paie canadienne.",
      link: 'Voir toutes les intégrations',
      caption: 'Comptabilité · RH · Temps et présences · Planification — plus une API ouverte',
      yourApp: 'Votre appli · API',
    },
    bento: {
      h: 'Tout ce que la paie exige. Rien de superflu.',
      heroTitle: 'Prête pour la conformité',
      heroBody:
        "Conformité et production électronique sur demande pour chaque province, y compris le Québec — feuillets de fin d'année et relevés d'emploi générés et transmis directement à partir de votre cycle de paie.",
      provLabel: 'Chaque province et territoire',
      docChips: ['T4', 'T4A', 'RL-1', 'RE', 'ARC en ligne', 'Revenu Québec'],
      cards: [
        {
          title: 'Paiements électroniques',
          body: "Paie par dépôt direct, avec remises à l'ARC calculées et payées automatiquement à chaque cycle.",
        },
        {
          title: 'Toutes les règles, à jour',
          body: "Tables d'impôt fédérales et provinciales, règles de jours fériés et calendriers de remise — mis à jour dès qu'ils changent.",
        },
        {
          title: 'Vos données vous appartiennent',
          body: 'Exportez talons, écritures de journal et historique complet en plusieurs formats, quand vous voulez. Aucun enfermement.',
        },
        {
          title: 'Adaptez votre système',
          body: 'Façonnez la paie selon vos livres comptables grâce aux options de mappage prédéfinies et personnalisées.',
        },
        {
          title: 'Une relation simple',
          body: 'Soutien illimité par de vrais spécialistes de la paie, inclus dans votre abonnement.',
        },
      ],
      link: 'Explorer toutes les fonctionnalités',
    },
    toolsH: 'Faites le calcul avant de vous inscrire',
    openTool: "Ouvrir l'outil",
    cta: {
      h: 'Voyez-le sur votre propre paie',
      sub: "Une visite guidée de 15 minutes avec questions — nous l'effectuerons avec vos propres chiffres de paie.",
      button: 'Réserver une démo de 15 min',
    },
  },
  features: {
    h: 'Conçu pour la paie canadienne, du début à la fin',
    sub: "Des heures brutes aux remises déclarées — sans tableurs ni devinettes pour l'ARC.",
    highlights: [
      {
        kicker: 'La paie, à votre façon',
        title: 'Payez chaque employé comme il le préfère',
        body: "Payez chacun comme il le préfère — par dépôt direct dans n'importe quel compte bancaire canadien, par chèque imprimé, ou un mélange des deux dans le même cycle de paie. Définissez la préférence de chaque employé une seule fois, et SimplePay l'applique à chaque paie — le jour de paie se fait tout seul.",
        points: [
          'Dépôt direct dans tout compte bancaire canadien',
          'Chèques prêts à imprimer sur chéquier standard',
          'Méthodes mixtes dans un même cycle — dépôt pour certains, chèques pour d\u2019autres',
          'Modifiez une paie pendant la collecte — les remboursements gardent la date de paie',
        ],
      },
      {
        kicker: 'Jours fériés, réglés',
        title: 'La paie des jours fériés, calculée pour vous',
        body: "Chaque province calcule la paie des jours fériés différemment — et le Québec, différemment encore. SimplePay applique automatiquement la bonne règle selon la province de chaque employé : un lundi férié ne se transforme jamais en dimanche de tableurs.",
        points: [
          'Règles provinciales intégrées — y compris le Québec',
          'Heures admissibles et salaire moyen calculés automatiquement',
          'Équipes horaires, salariées ou mixtes',
          'Des réponses, pas des tables de consultation',
        ],
      },
      {
        kicker: 'Conformité sur demande',
        title: 'Chaque province. Chaque règle. Toujours à jour.',
        body: "Tables d'impôt fédérales et provinciales, RPC/RPC2, AE et exigences de déclaration sont maintenus pour vous — pour toutes les provinces et tous les territoires, y compris le Québec (RL-1, CNESST). Imprimez ou produisez électroniquement vos documents de conformité quand vous en avez besoin, directement du cycle de paie.",
        points: [
          'Production électronique T4 / T4A / RL-1 / RE',
          'Papier ou électronique — votre choix',
          'RPC2 et dernières mises à jour de l\u2019ARC',
          'Québec pleinement pris en charge, pas en surcroît',
        ],
      },
      {
        kicker: 'Remises en pilote automatique',
        title: 'Remises ARC, RQ et EHT — déclarées selon votre calendrier',
        body: "Votre fréquence de remise n'est pas votre fréquence de paie, et SimplePay le sait. Mensuelle, trimestrielle ou Seuil 1 / Seuil 2 — les remises sont calculées à chaque cycle de paie et déclarées selon le calendrier que l'ARC vous a assigné.",
        points: [
          'Mensuel, trimestriel, Seuil 1 et 2',
          'ARC et Revenu Québec sur des calendriers distincts',
          'EHT gérée avec les retenues à la source',
          'Déclaré pour vous — aucun portail ARC',
        ],
      },
      {
        kicker: 'RE sans panique',
        title: 'Des RE émis dans le délai de 5 jours',
        body: "Quand quelqu'un part, le compte à rebours commence : Service Canada exige le RE dans les 5 jours ouvrables. SimplePay signale la cessation d'emploi, suit le délai et vous remet un document prêt à produire — ou le prépare pour vous en PDF téléchargeable, habituellement en un jour.",
        points: [
          'Délai suivi dès la date de cessation',
          'Impression ou production électronique',
          'Option gérée par SimplePay · 3 $ par RE',
          'Historique complet, toujours accessible',
        ],
      },
      {
        kicker: 'Votre paie, à votre image',
        title: 'Gains, retenues et remboursements personnalisés',
        body: "Partez des éléments de paie prédéfinis ou créez les vôtres — avantages imposables, indemnités, saisies, remboursements de dépenses. Des formules peuvent remplir automatiquement les colonnes de paie : les calculs récurrents se saisissent tout seuls.",
        points: [
          'Éléments de paie prédéfinis et personnalisés',
          'Formules qui remplissent les colonnes de paie',
          'Avantages imposables et remboursements bien traités',
          'Défini une fois — appliqué à chaque cycle',
        ],
      },
      {
        kicker: 'Pensé pour grandir',
        title: 'Emplacements et structure pour les équipes en croissance',
        body: "Organisez vos employés par emplacement, service ou entité, et produisez des rapports à tous les niveaux. Le siège social voit l'ensemble; les gestionnaires d'emplacement voient le leur — avec des rôles d'accès assortis.",
        points: [
          'Organisation multi-emplacements',
          'Rapports consolidés ou par emplacement',
          'Rôles d\u2019accès personnalisés',
          'Fonctionne avec la vue de votre comptable',
        ],
      },
      {
        kicker: 'Coûts par projet, de bout en bout',
        title: 'Feuilles de temps entrantes. Écritures imputées sortantes.',
        body: "Importez les feuilles de temps avec codes de projet de vos outils de planification, puis exportez la paie vers la comptabilité avec les mêmes codes. Les coûts de main-d'œuvre atterrissent sur le bon projet dans QuickBooks ou Xero — sans ressaisir une seule heure.",
        points: [
          'Importation de feuilles de temps avec codes de projet',
          'Écritures de journal imputées par projet',
          'QuickBooks Online, Xero et plus',
          'Mappage personnalisé du grand livre inclus',
        ],
      },
    ],
    placeholder: "Espace réservé pour une capture d'écran du produit",
    chip1: 'Un même cycle · 3 chèques + 1 dépôt direct',
    includedH: 'Un prix. Tout inclus.',
    includedSub: "Aucun niveau. Aucun mur payant pour l'essentiel.",
    included: [
      'Soutien illimité',
      'Intégrations de modules complémentaires',
      "Accès à l'API",
      'Calculateur de jours fériés',
      'Dépôt direct',
      'Gains et retenues personnalisés',
      'Importation du grand livre',
    ],
    cta: 'Voir les tarifs',
  },
  pricing: {
    h: 'Une tarification juste et transparente',
    sub: "2 $ par employé par cycle de paie. Sans contrat, sans frais d'installation ou de résiliation, sans frais annuels.",
    perEmployee: ' / employé / cycle de paie',
    minLabel: (min: number) => `Facturation minimale de ${min} $ par cycle de paie`,
    tiers: [
      {
        name: 'Standard',
        blurb: "Tout ce qu'il faut pour faire la paie — bien fait, simplement.",
        badge: '',
        features: [
          'Paie par dépôt direct',
          'Toutes les provinces et tous les territoires, y compris le Québec',
          'Production électronique T4, T4A, RL-1 et RE',
          'Calculateur de jours fériés',
          "Intégrations et accès à l'API",
          'Soutien illimité',
          "7 ans d'historique téléchargeable",
        ],
      },
      {
        name: 'Professionnel',
        blurb: 'Pour les équipes qui gèrent des tâches et des projets dans la paie.',
        badge: 'Pour les équipes par projet',
        features: [
          'Tout ce qui est inclus dans Standard',
          'Importation des tâches / projets à partir des feuilles de temps',
          "Exportation d'écritures de journal par tâche / projet vers votre application comptable",
          "Création de rôles d'accès personnalisés",
          'Formules pour remplir les colonnes de paie',
          "Structure d'entreprise par emplacements",
        ],
      },
    ],
    est: {
      h: 'Estimez votre coût',
      sub: 'De vrais chiffres, votre paie — aucun courriel requis.',
      plan: 'Forfait',
      minRun: (min: number) => `min ${min} $ / cycle`,
      freq: 'Fréquence de paie',
      frequencies: ['Mensuelle', 'Bimensuelle', 'Aux deux semaines', 'Hebdomadaire'],
      employees: 'Employés payés par cycle',
      remit: 'Remises par SimplePay',
      remitNote: '(optionnel, 5 $ chacune par remise)',
      remitFreq: 'Fréquence des remises',
      remitTypes: ['ARC', 'EHT', 'RQ'],
      remitFrequencies: ['Trimestrielle', 'Mensuelle', 'Seuil 1', 'Seuil 2'],
      perRun: 'Par cycle de paie',
      perYear: (label: string, runs: number) => `Par année · ${label.toLowerCase()} (${runs} cycles)`,
      minApplies: (emp: number, per: number, min: number, tier: string) =>
        `${emp} × 2 $ = ${per} $ → le minimum de ${min} $ du forfait ${tier} s'applique`,
      normal: (emp: number, per: number) => `${emp} employés × 2 $ = ${per} $`,
      annualRemit: (n: number, total: number) =>
        `comprend ${total} $ de remises (${n} × 5 $ à leurs propres fréquences)`,
      foot: "Seuls les employés payés dans un cycle sont facturés (chèque ou dépôt direct) — les employés inactifs ne sont jamais facturés. Le RE par SimplePay coûte 3 $ par RE émis, seulement lorsque vous en avez besoin.",
    },
    optional: {
      h: 'Services optionnels',
      remitName: 'Remises par SimplePay',
      remitNote: 'ARC, EHT et RQ — chaque remise facturée séparément',
      remitPer: 'par remise',
      roeName: 'RE par SimplePay',
      roeNote: "Seulement lorsque vous nous demandez d'en émettre un",
      roePer: 'par RE émis',
    },
    includedH: 'Inclus dans chaque abonnement',
    included: [
      'Soutien illimité',
      'Intégrations de modules complémentaires',
      "Accès à l'API",
      'Calculateur de jours fériés',
      'Dépôt direct',
    ],
    noFees: {
      h: 'Aucuns autres frais. Point final.',
      body: "Aucuns frais d'installation, de contrat, de résiliation, de conformité annuelle ou de paie inactive — ils ont disparu pour de bon.",
    },
    cta: {
      h: 'Faites le calcul avec votre propre paie',
      sub: "Commencez dès aujourd'hui — ou réservez une visite guidée de 15 minutes et nous chiffrerons cela ensemble.",
      start: 'Commencer',
      demo: 'Réserver une démo de 15 min',
    },
  },
  integrations: {
    h: 'Connexion. Contrôle. Réussite.',
    sub: (count: number) =>
      `SimplePay se connecte en toute transparence aux principaux systèmes de RH, de suivi du temps, de planification et de comptabilité — ${count} intégrations, et ce n'est pas fini.`,
    cats: [
      { name: 'RH', blurb: 'Gardez les dossiers des employés synchronisés — aucune double saisie.' },
      { name: 'Comptabilité', blurb: 'Envoyez vos écritures de paie directement dans votre grand livre.' },
      { name: 'Temps, présences et planification', blurb: 'Les heures approuvées passent directement dans le cycle de paie.' },
      { name: "Fin d'année et production", blurb: "Clôturez l'année sans acrobaties de tableur." },
    ],
    apps: (n: number) => `${n} ${n === 1 ? 'application' : 'applications'}`,
    website: 'Site Web',
    guide: "Guide d'intégration",
    capabilities: {
      'Import / export employees': "Importation / exportation d'employés",
      'Sync employee data': 'Synchronisation des données des employés',
      'Import timesheets': 'Importation des feuilles de temps',
      'Import employees': "Importation d'employés",
      'Payroll journal with multi-wage accounts': 'Journal de paie avec comptes de salaires multiples',
      'Import / export employees and sync': "Importation / exportation et synchronisation d'employés",
      'Single Sign-On (SSO)': 'Authentification unique (SSO)',
      'Tracking categories': 'Catégories de suivi',
      'Export payroll journal': 'Exportation du journal de paie',
      'Payroll journal (desktop & online)': 'Journal de paie (bureau et en ligne)',
      'Import timesheets (+ scheduling)': 'Importation des feuilles de temps (+ planification)',
      'Export payroll year-to-date': 'Exportation des cumuls annuels de paie',
      'T4 / RL-1 filing': 'Production des T4 / RL-1',
      PIER: 'PIER',
    } as Record<string, string>,
    cta: {
      h: 'Vous ne voyez pas votre application préférée ?',
      sub: "Contactez-nous — avec le mappage personnalisé et l'accès à l'API ouverte, il y a de fortes chances que nous puissions la connecter.",
      button: 'Contactez-nous',
    },
  },
  tools: {
    h: 'Des utilitaires de paie, sans inscription',
    sub: 'Le même moteur de calcul qui propulse SimplePay — gratuit à utiliser.',
    openTool: "Ouvrir l'outil",
    cta: {
      h: 'Vous aimez le calcul ? Automatisez le reste.',
      sub: 'La calculatrice fait un cycle de paie. SimplePay les fait tous — plus les déclarations, les dépôts et les dossiers.',
      button: 'Voir les tarifs',
    },
  },
  toolPage: {
    back: 'Tous les outils gratuits',
    workspace: (title: string) => `${title} — espace de travail`,
    workspaceNote:
      "L'outil interactif de simplepay.ca sera intégré à cette page dans la version finale.",
    does: "Ce qu'il fait",
    audience: 'Pour qui ?',
    more: "Plus d'outils gratuits",
    openTool: "Ouvrir l'outil",
    cta: {
      h: 'Vous aimez le calcul ? Automatisez le reste.',
      sub: 'Cet outil fait un travail. SimplePay fait toute la paie — déclarations, dépôts et dossiers inclus.',
      button: 'Voir les tarifs',
    },
  },
  login: {
    title: 'Connexion à SimplePay',
    sub: "Bon retour. Reprenez votre paie là où vous l'aviez laissée.",
    email: 'Courriel professionnel',
    password: 'Mot de passe',
    remember: 'Se souvenir de moi',
    forgot: 'Mot de passe oublié ?',
    submit: 'Connexion',
    noAccount: 'Nouveau chez SimplePay ?',
    start: 'Lancez votre paie',
  },
  bookDemo: {
    title: 'Réservez votre démo de 15 minutes',
    sub: "Voyez SimplePay avec votre propre paie — une visite en partage d'écran, sans diaporama.",
    name: 'Nom',
    email: 'Courriel professionnel',
    company: 'Entreprise',
    time: 'Moment préféré',
    timeOptions: ['Matin (en semaine)', 'Après-midi (en semaine)', 'Soir (en semaine)'],
    submit: 'Demander ma démo',
    note: "15 minutes · Partage d'écran · Aucun diaporama",
    alt: 'Vous préférez le courriel ?',
  },
  pro: {
    h: 'Partenaires Pro SimplePay',
    sub: 'Comptables, teneurs de livres et fournisseurs de services qui traitent la paie sur SimplePay — un soutien qui va au-delà du logiciel.',
    filterAll: 'Tout',
    visit: 'Visiter le site',
    become: {
      h: 'Vous gérez la paie de vos clients ?',
      sub: "Rejoignez le programme Partenaires Pro — gérez plusieurs clients à partir d'un seul compte, avec tarification partenaire et soutien prioritaire.",
      button: 'Devenir Partenaire Pro',
    },
  },
}

export const translations: Record<'en' | 'fr', Translation> = { en, fr }
